import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Alert,
  Box,
  Stack,
  Divider,
  IconButton,
  Skeleton,
  Paper,
  Select,
  MenuItem,
  TextField,
  Collapse,
  Tooltip,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Close as CloseIcon,
  EmailOutlined,
  PersonOutline,
  CalendarToday,
  Label as LabelIcon,
  Category as CategoryIcon,
  FiberManualRecord,
  WarningAmber,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  History as HistoryIcon,
  VerifiedUser as VerifiedUserIcon,
  Timeline as TimelineIcon,
  Reply as ReplyIcon,
  Forward as ForwardIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  AccountTree as ThreadIcon,
} from '@mui/icons-material';
import React, { useMemo, useState } from 'react';
import API from '../../../../API';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { alpha } from '@mui/material/styles';

const VALID_CATEGORIES = [
  'Paid',
  'Affiliate',
  'Agency',
  'Platform',
  'Gifting',
  'Music',
  'General',
  'Meeting',
  'External',
  'APAC',
  'FanMail',
  'ignore-thread'
];

const CATEGORY_COLORS = {
  'Paid': 'success',
  'Affiliate': 'info',
  'Agency': 'secondary',
  'Platform': 'primary',
  'Gifting': 'warning',
  'Music': 'error',
  'General': 'default',
  'Meeting': 'info',
  'External': 'warning',
  'APAC': 'secondary',
  'FanMail': 'primary',
  'ignore-thread': 'default'
};

const REPLY_TYPE_LABELS = {
  'initial_reply': 'Initial Reply',
  'followup_2_4_days': 'Follow-up (2-4 days)',
  'followup_5_plus_days': 'Follow-up (5+ days)',
  'cc_manager': 'CC Manager'
};

const EMAIL_STAGES = {
  'received': { label: 'Received', color: '#2196F3', icon: <EmailOutlined /> },
  'replied': { label: 'Replied', color: '#4CAF50', icon: <ReplyIcon /> },
  'followup_sent': { label: 'Follow-up Sent', color: '#9C27B0', icon: <ForwardIcon /> },
  'cc_manager': { label: 'Manager CC\'d', color: '#FF9800', icon: <PersonOutline /> },
  'closed': { label: 'Closed', color: '#757575', icon: <CheckCircleIcon /> }
};

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value, highlight = false }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 0.75 }}>
    <Icon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.3 }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: highlight ? 'primary.main' : 'text.primary',
          fontWeight: highlight ? 600 : 400,
          wordBreak: 'break-word',
          mt: 0.25
        }}
      >
        {value || 'N/A'}
      </Typography>
    </Box>
  </Stack>
);

export default function BrandEmailDialog({ email, keywords, onClose }) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const { data: message, error: messageError, isLoading: messageLoading } = useQuery({
    queryKey: ['creatorEmail', email],
    queryFn: () => API.creators.adminBrandEmail(email),
    enabled: !!email,
    staleTime: 1000.0 * 60 * 60,
    refetchOnWindowFocus: false,
  });

  // NEW: Fetch thread information
  const { data: threadData, isLoading: threadLoading, refetch: refetchThread } = useQuery({
    queryKey: ['emailThread', email],
    queryFn: () => API.blitzAI.getEmailThread(email, true),
    enabled: !!email && !!message?.message?.thread_id,
    staleTime: 1000.0 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['reclassificationHistory', email],
    queryFn: () => API.blitzAI.getReclassificationHistory(email),
    enabled: !!email && showHistory,
    staleTime: 1000.0 * 60 * 5,
  });

  const reclassifyMutation = useMutation({
    mutationFn: ({ emailId, data }) => API.blitzAI.manuallyReclassifyEmail(emailId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['creatorEmail', email]);
      queryClient.invalidateQueries(['reclassificationHistory', email]);
      queryClient.invalidateQueries(['creatorEmails']);
      setIsEditing(false);
      setReason('');
      setNotes('');
    },
  });

  const emailBody = useMemo(() => {
    if (!message) return null;
    
    const possibleBodies = [
      message.body,
      message.message?.body,
      message.message?.snippet,
      message.snippet
    ];
    
    for (const body of possibleBodies) {
      if (body && typeof body === 'string' && body.trim().length > 0) {
        return body;
      }
    }
    
    return null;
  }, [message]);

  const categoryData = useMemo(() => {
    if (!!message && !!emailBody && !!keywords && Array.isArray(keywords)) {
      for (let catIdx = 0; catIdx < keywords.length; catIdx++) {
        let category = keywords[catIdx];
        for (let wordIdx = 0; wordIdx < category.keywords.length; wordIdx++) {
          const word = category.keywords[wordIdx].toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const pattern = new RegExp(`\\b${word}\\b`, 'i');
          const match = emailBody.match(pattern);
          if (match) {
            return {
              category: category.category,
              keyword: word,
              index: match.index
            };
          }
        }
      }
      return { category: null, keyword: null, index: -1 };
    }
    return undefined;
  }, [message, emailBody, keywords]);

  const splitText = useMemo(() => {
    if (!categoryData || !emailBody) {
      return { before: emailBody || '', high: '', after: '' };
    }

    if (!categoryData.category) {
      return { before: emailBody, high: '', after: '' };
    }

    return {
      before: emailBody.slice(0, categoryData.index),
      high: emailBody.slice(categoryData.index, categoryData.index + categoryData.keyword.length),
      after: emailBody.slice(categoryData.index + categoryData.keyword.length)
    };
  }, [categoryData, emailBody]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleStartEdit = () => {
    setNewCategory(message?.message?.category || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewCategory('');
    setReason('');
    setNotes('');
  };

  const handleSaveReclassification = () => {
    if (!newCategory || !reason.trim()) {
      return;
    }

    reclassifyMutation.mutate({
      emailId: email,
      data: {
        new_category: newCategory,
        reason: reason.trim(),
        notes: notes.trim() || undefined
      }
    });
  };

  const currentCategory = message?.message?.category;
  const isManuallyClassified = !!message?.message?.reclassified_by;
  const originalCategory = message?.message?.original_category;
  
  // NEW: Thread information
  const emailStage = message?.message?.email_stage || 'received';
  const threadPosition = message?.message?.thread_position || 1;
  const totalThreadMessages = message?.message?.total_thread_messages || 1;
  const ourReplyCount = message?.message?.our_reply_count || 0;
  const stageInfo = EMAIL_STAGES[emailStage] || EMAIL_STAGES['received'];

  // Get our replies from thread data
  const ourReplies = threadData?.thread?.our_replies || [];
  const gmailMessages = threadData?.thread?.gmail_messages || [];
  const hasThread = threadData && threadData.thread;

  return (
    <Dialog
      open={!!email}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <EmailOutlined color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Email Details & Thread
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {email}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Thread Position Badge */}
          {totalThreadMessages > 1 && (
            <Tooltip title={`Email ${threadPosition} of ${totalThreadMessages} in thread`}>
              <Chip
                icon={<ThreadIcon />}
                label={`${threadPosition}/${totalThreadMessages}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Tooltip>
          )}
          {/* Reply Count Badge */}
          {ourReplyCount > 0 && (
            <Tooltip title={`${ourReplyCount} automated ${ourReplyCount === 1 ? 'reply' : 'replies'} sent`}>
              <Chip
                icon={<ReplyIcon />}
                label={ourReplyCount}
                size="small"
                color="success"
              />
            </Tooltip>
          )}
          {/* Stage Badge */}
          <Tooltip title="Current processing stage">
            <Chip
              icon={stageInfo.icon}
              label={stageInfo.label}
              size="small"
              sx={{
                bgcolor: stageInfo.color,
                color: 'white',
                fontWeight: 600
              }}
            />
          </Tooltip>
          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {messageLoading ? (
          <Box sx={{ p: 3 }}>
            <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 1 }} />
            <Skeleton variant="text" width="60%" sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" />
          </Box>
        ) : messageError ? (
          <Alert severity="error" sx={{ m: 3 }}>
            Failed to load email details. Please try again.
          </Alert>
        ) : (
          <Box>
            {/* NEW: Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ px: 3 }}>
                <Tab label="Email Details" icon={<EmailOutlined />} iconPosition="start" />
                {hasThread && ourReplies.length > 0 && (
                  <Tab 
                    label={`Our Replies (${ourReplies.length})`} 
                    icon={<ReplyIcon />} 
                    iconPosition="start" 
                  />
                )}
                {hasThread && gmailMessages.length > 1 && (
                  <Tab 
                    label={`Full Thread (${gmailMessages.length})`} 
                    icon={<TimelineIcon />} 
                    iconPosition="start" 
                  />
                )}
              </Tabs>
            </Box>

            {/* Tab 0: Email Details (Original Content) */}
            <TabPanel value={tabValue} index={0}>
              <Stack spacing={0}>
                {/* Legacy/Missing Body Alerts */}
                {message?.is_legacy && (
                  <Alert severity="info" icon={<FiberManualRecord sx={{ fontSize: 16 }} />} sx={{ m: 3, mb: 0, borderRadius: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Legacy Email</Typography>
                    <Typography variant="caption" color="text.secondary">
                      From deprecated forwarding system. Full email body unavailable.
                    </Typography>
                  </Alert>
                )}

                {!emailBody && !message?.is_legacy && (
                  <Alert severity="warning" icon={<WarningAmber sx={{ fontSize: 16 }} />} sx={{ m: 3, mb: 0, borderRadius: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Email Body Unavailable</Typography>
                    <Typography variant="caption" color="text.secondary">
                      The email body could not be retrieved.
                    </Typography>
                  </Alert>
                )}

                {/* Subject Card */}
                <Paper elevation={0} sx={{ m: 3, mt: (message?.is_legacy || !emailBody) ? 2 : 3, p: 2.5, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04), border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
                  <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 1 }}>
                    Subject
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600, lineHeight: 1.4 }}>
                    {message?.message?.subject || 'No Subject'}
                  </Typography>
                </Paper>

                {/* Email Details */}
                <Box sx={{ px: 3, pb: 2 }}>
                  <Stack spacing={1.5}>
                    <InfoRow icon={PersonOutline} label="From" value={message?.message?.sender} highlight />
                    <InfoRow icon={PersonOutline} label="To" value={message?.message?.receiver} />
                    <InfoRow icon={CalendarToday} label="Received" value={formatDate(message?.message?.date_received)} />
                    <InfoRow icon={CategoryIcon} label="Thread ID" value={message?.message?.thread_id} />
                  </Stack>
                </Box>

                <Divider />

                {/* Email Body */}
                <Box sx={{ px: 3, py: 3 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 1, mb: 2, display: 'block' }}>
                    Message Body
                  </Typography>
                  {!emailBody ? (
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 1.5, bgcolor: (theme) => alpha(theme.palette.warning.main, 0.05), borderColor: (theme) => alpha(theme.palette.warning.main, 0.2), textAlign: 'center' }}>
                      <WarningAmber sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Email body could not be displayed
                      </Typography>
                    </Paper>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, bgcolor: (theme) => alpha(theme.palette.background.default, 0.5), maxHeight: '300px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.875rem', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {message?.is_legacy ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontFamily: 'inherit' }}>
                          {emailBody}
                        </Typography>
                      ) : (
                        <>
                          <Typography component="span" sx={{ fontFamily: 'inherit' }}>
                            {splitText.before}
                          </Typography>
                          {splitText.high && (
                            <Typography component="span" sx={{ fontFamily: 'inherit', color: '#1a5d1a', backgroundColor: '#9ef01a', px: 0.5, py: 0.25, borderRadius: 0.5, fontWeight: 600 }}>
                              {splitText.high}
                            </Typography>
                          )}
                          <Typography component="span" sx={{ fontFamily: 'inherit' }}>
                            {splitText.after}
                          </Typography>
                        </>
                      )}
                    </Paper>
                  )}
                </Box>

                <Divider />

                {/* Category & Reclassification (keeping original code) */}
                <Box sx={{ px: 3, py: 3 }}>
                  <Stack spacing={2.5}>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CategoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 1 }}>
                            Category
                          </Typography>
                        </Stack>
                        {!isEditing && (
                          <Stack direction="row" spacing={1}>
                            {isManuallyClassified && (
                              <Tooltip title={`Manually classified by admin on ${formatDate(message?.message?.reclassified_at)}`}>
                                <Chip icon={<VerifiedUserIcon />} label="Manually Classified" size="small" color="info" variant="outlined" sx={{ fontSize: '0.7rem', height: 24 }} />
                              </Tooltip>
                            )}
                            <Tooltip title="Edit category">
                              <IconButton size="small" onClick={handleStartEdit} sx={{ ml: 1 }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View history">
                              <IconButton size="small" onClick={() => setShowHistory(!showHistory)}>
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </Stack>

                      {!isEditing ? (
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip label={currentCategory || 'Uncategorized'} color={CATEGORY_COLORS[currentCategory] || 'default'} sx={{ fontWeight: 600, borderRadius: 1.5, px: 0.5 }} />
                          {categoryData?.keyword && !isManuallyClassified && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              • Keyword: <Box component="span" sx={{ fontWeight: 600, fontStyle: 'italic' }}>"{categoryData.keyword}"</Box>
                            </Typography>
                          )}
                          {isManuallyClassified && originalCategory && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              • Original: <Box component="span" sx={{ fontWeight: 600 }}>{originalCategory}</Box>
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: (theme) => alpha(theme.palette.info.main, 0.03), borderColor: (theme) => alpha(theme.palette.info.main, 0.2) }}>
                          <Stack spacing={2}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                New Category *
                              </Typography>
                              <Select fullWidth size="small" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} sx={{ borderRadius: 1 }}>
                                {VALID_CATEGORIES.map((cat) => (
                                  <MenuItem key={cat} value={cat}>
                                    <Chip label={cat} size="small" color={CATEGORY_COLORS[cat] || 'default'} sx={{ fontWeight: 600 }} />
                                  </MenuItem>
                                ))}
                              </Select>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                Reason *
                              </Typography>
                              <TextField fullWidth size="small" multiline rows={2} placeholder="Why are you reclassifying this email?" value={reason} onChange={(e) => setReason(e.target.value)} />
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                Additional Notes (Optional)
                              </Typography>
                              <TextField fullWidth size="small" multiline rows={2} placeholder="Any additional context..." value={notes} onChange={(e) => setNotes(e.target.value)} />
                            </Box>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button size="small" onClick={handleCancelEdit} startIcon={<CancelIcon />}>
                                Cancel
                              </Button>
                              <Button size="small" variant="contained" onClick={handleSaveReclassification} startIcon={<SaveIcon />} disabled={!newCategory || !reason.trim() || reclassifyMutation.isLoading}>
                                {reclassifyMutation.isLoading ? 'Saving...' : 'Save'}
                              </Button>
                            </Stack>
                            {reclassifyMutation.isError && (
                              <Alert severity="error" sx={{ mt: 1 }}>
                                Failed to reclassify email. Please try again.
                              </Alert>
                            )}
                          </Stack>
                        </Paper>
                      )}
                    </Box>

                    {/* Reclassification History */}
                    <Collapse in={showHistory}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: (theme) => alpha(theme.palette.background.default, 0.5) }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                          Reclassification History
                        </Typography>
                        {historyLoading ? (
                          <Stack spacing={1}>
                            <Skeleton variant="text" />
                            <Skeleton variant="text" />
                          </Stack>
                        ) : !history || history.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No reclassification history available
                          </Typography>
                        ) : (
                          <Stack spacing={1.5}>
                            {history.map((entry, idx) => (
                              <Box key={idx} sx={{ p: 1.5, borderRadius: 1, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03), borderLeft: (theme) => `3px solid ${theme.palette.primary.main}` }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                  <Chip label={entry.old_category} size="small" color={CATEGORY_COLORS[entry.old_category] || 'default'} sx={{ fontSize: '0.7rem', height: 20 }} />
                                  <Typography variant="caption" color="text.secondary">→</Typography>
                                  <Chip label={entry.new_category} size="small" color={CATEGORY_COLORS[entry.new_category] || 'default'} sx={{ fontSize: '0.7rem', height: 20 }} />
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  {formatDate(entry.changed_at)} • {entry.changed_by}
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                  "{entry.reason}"
                                </Typography>
                                {entry.notes && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    Note: {entry.notes}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Paper>
                    </Collapse>

                    {/* Labels */}
                    {message?.labels && message.labels.length > 0 && (
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                          <LabelIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 1 }}>
                            Labels
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {message.labels.map((label) => (
                            <Chip key={label} label={label} size="small" variant="outlined" sx={{ borderRadius: 1.5, fontWeight: 500, fontSize: '0.75rem' }} />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </TabPanel>

            {/* Tab 1: Our Replies */}
            {hasThread && ourReplies.length > 0 && (
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ p: 3 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Our Automated Replies
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {ourReplies.length} {ourReplies.length === 1 ? 'reply' : 'replies'} sent by our system
                      </Typography>
                    </Box>
                    <Tooltip title="Refresh thread data">
                      <IconButton onClick={() => refetchThread()} disabled={threadLoading}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  {threadLoading ? (
                    <Stack spacing={2}>
                      <Skeleton variant="rectangular" height={120} />
                      <Skeleton variant="rectangular" height={120} />
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      {ourReplies.map((reply, idx) => (
                        <Card key={reply.id} variant="outlined">
                          <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="start" sx={{ mb: 2 }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                  icon={<ReplyIcon />}
                                  label={REPLY_TYPE_LABELS[reply.reply_type] || reply.reply_type}
                                  size="small"
                                  color="success"
                                  sx={{ fontWeight: 600 }}
                                />
                                {reply.confidence && (
                                  <Chip
                                    label={`${(reply.confidence * 100).toFixed(0)}% confidence`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(reply.date_sent)}
                              </Typography>
                            </Stack>

                            {reply.content_preview && (
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.02),
                                  borderColor: (theme) => alpha(theme.palette.success.main, 0.2),
                                  borderRadius: 1.5
                                }}
                              >
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                  {reply.content_preview}
                                </Typography>
                              </Paper>
                            )}

                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                Category: <strong>{reply.category || 'N/A'}</strong>
                              </Typography>
                              {reply.message_id && (
                                <Typography variant="caption" color="text.secondary">
                                  Message ID: {reply.message_id}
                                </Typography>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Box>
              </TabPanel>
            )}

            {/* Tab 2: Full Gmail Thread */}
            {hasThread && gmailMessages.length > 1 && (
              <TabPanel value={tabValue} index={2}>
                <Box sx={{ p: 3 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Complete Gmail Thread
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {gmailMessages.length} messages in conversation
                      </Typography>
                    </Box>
                    <Tooltip title="Refresh thread data">
                      <IconButton onClick={() => refetchThread()} disabled={threadLoading}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    This shows the complete conversation including external replies and our automated responses
                  </Alert>

                  {threadLoading ? (
                    <Stack spacing={2}>
                      <Skeleton variant="rectangular" height={100} />
                      <Skeleton variant="rectangular" height={100} />
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      {gmailMessages.map((msg, idx) => (
                        <Card
                          key={msg.id}
                          variant="outlined"
                          sx={{
                            bgcolor: msg.is_from_us
                              ? (theme) => alpha(theme.palette.success.main, 0.05)
                              : 'background.paper',
                            borderLeft: msg.is_from_us
                              ? (theme) => `4px solid ${theme.palette.success.main}`
                              : 'none'
                          }}
                        >
                          <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="start" sx={{ mb: 2 }}>
                              <Box>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {msg.sender}
                                  </Typography>
                                  {msg.is_from_us && (
                                    <Chip
                                      label="Sent by us"
                                      size="small"
                                      color="success"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  To: {msg.receiver}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(msg.date)}
                              </Typography>
                            </Stack>

                            {msg.subject && (
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                {msg.subject}
                              </Typography>
                            )}

                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                bgcolor: 'background.default',
                                borderRadius: 1
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {msg.snippet || 'No content preview available'}
                              </Typography>
                            </Paper>

                            {msg.labels && msg.labels.length > 0 && (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                {msg.labels.map((label) => (
                                  <Chip
                                    key={label}
                                    label={label}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                ))}
                              </Stack>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Box>
              </TabPanel>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: (theme) => alpha(theme.palette.background.default, 0.5) }}>
        <Button onClick={onClose} variant="contained" disableElevation sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, px: 3 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}